import {useCallback, useEffect, useMemo, useRef} from 'react';
import {Edge, Node, ReactFlowInstance, useStore} from 'reactflow';

import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  FLOWCHART_CONFIG_INITIAL_LOAD_ENDED_EVENT,
  FLOWCHART_CONFIG_INITIAL_LOAD_DATA_ENDED_EVENT,
  FLOWCHART_CONFIG_INITIAL_RENDER_STARTED_EVENT,
  FLOWCHART_CONFIG_INITIAL_RENDER_ENDED_EVENT,
  composeFlowchartEventMetadata,
} from 'performance/processProcessesEvents';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {DEFAULT_FIT_VIEW_OPTIONS} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {
  PROCESS_FLOWCHART_EDGE_LABEL_SIZE,
  PROCESS_FLOWCHART_EDGE_LABEL_WEIGHT,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {FONT_FAMILY_PROXIMA} from 'styles/FontConstants';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';
import {FIT_VIEW_MAX_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {useGetNodeHandlesData} from 'ui-library/reactflow/handles/useGetNodeHandlesData';
import {LayoutAlgorithmName} from 'ui-library/reactflow/layout/algorithms';
import {
  DEFAULT_ELK_LAYOUT_DIRECTION,
  LayoutSettings,
} from 'ui-library/reactflow/layout/calculateDimensionsForLayout';
import {hideFlowchartElementBeforeLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';
import layoutFlowchart from 'ui-library/reactflow/layout/layoutFlowchart';
import {getTextDimensions} from 'util/getTextDimensions';

import {countEdgesByType, FlowchartEdge, SNAP_GRID} from './processFlowchartConfigEditorConstants';
import {FlowchartNode, isInteractiveTaskNode, TaskSchemaNode} from './processFlowchartConfigEditorNodeTypes';
import {useWaitForLayoutReady} from './useFlowchartState/useWaitForLayoutReady';

interface UseFlowchartLayoutParams {
  interactiveNodes: Array<FlowchartNode>;
  edgesForLayout: Array<FlowchartEdge>;
  isExecutionViewer: boolean;
  getMeasuredDimensionsFromStore: (nodeId: string) => {width: number; height: number};
  setNodesAndEdgesViaChangesForLayout: (newNodes: Array<Node>, newEdges: Array<Edge>) => void;
  reactFlowInstance: ReactFlowInstance;
  flowchartTypeName: string;
  editorMode: FlowchartEditorMode;
  flowchartConfigId: string;
}

interface UseFlowchartLayoutReturn {
  nodesToRender: Array<FlowchartNode>;
  edgesToRender: Array<FlowchartEdge>;
  startNode: TaskSchemaNode | null;
  endNode: TaskSchemaNode | null;
  getCustomLayoutSettings: () => Partial<LayoutSettings<FlowchartNode, Edge>>;
  getHandles: ReturnType<typeof useGetNodeHandlesData>;
  isLayoutInProgress: boolean;
  runWhenLayoutReady: <T>(layoutFn: () => Promise<T>) => Promise<T>;
}

/**
 * Orchestrates the ELK-based auto-layout algorithm for the flowchart, including initial layout
 * on mount, re-layout on node/edge changes, fit-to-view behavior, and performance profiling.
 */
export default function useFlowchartLayout({
  interactiveNodes,
  edgesForLayout,
  isExecutionViewer,
  getMeasuredDimensionsFromStore,
  setNodesAndEdgesViaChangesForLayout,
  reactFlowInstance,
  flowchartTypeName,
  editorMode,
  flowchartConfigId,
}: UseFlowchartLayoutParams): UseFlowchartLayoutReturn {
  const hasPerformedInitialAutolayout = useRef(false);
  const hasLoggedNodesRenderedRef = useRef(false);
  const wasHiddenDuringRenderRef = useRef(document.hidden);
  const [performInitialFitView, setPerformInitialFitView] = useStateIfMounted(false);

  // Record the start and end nodes for use in layout.
  const startNode = useMemo(
    () =>
      interactiveNodes.find(
        (node): node is TaskSchemaNode =>
          isInteractiveTaskNode(node) &&
          node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
      ) ?? null,
    [interactiveNodes]
  );

  const endNode = useMemo(
    () =>
      interactiveNodes.find(
        (node): node is TaskSchemaNode =>
          isInteractiveTaskNode(node) &&
          node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
      ) ?? null,
    [interactiveNodes]
  );

  const getHandles = useGetNodeHandlesData();

  const allNodesReadyForAutolayout = useStore(
    useCallback(() => {
      if (hasPerformedInitialAutolayout.current || interactiveNodes.length === 0) {
        return false;
      }

      // Check if all nodes have handles. In testing we found that on mount the reactflow nodes
      // are not always finished initializing. Checking handles is a proxy for us to determine if node
      // state is fully initialized.
      const nodesWithoutHandles = interactiveNodes.filter((node) => getHandles(node.id).length === 0);

      return nodesWithoutHandles.length === 0;
    }, [interactiveNodes, getHandles])
  );

  // Used by layout system to get the dimensions of edge labels.
  // todo(BNCH-137336): Possibly make this method for calculating label dimensions the default
  // for our internal ReactFlow library.
  const calculateEdgeLabelDimensions = useCallback((edge: Edge): {width: number; height: number} => {
    if (edge.label == null || typeof edge.label !== 'string') {
      return {width: 0, height: 0};
    }
    const {width, height} = getTextDimensions(
      edge.label,
      PROCESS_FLOWCHART_EDGE_LABEL_WEIGHT,
      PROCESS_FLOWCHART_EDGE_LABEL_SIZE,
      FONT_FAMILY_PROXIMA
    );

    return {
      width,
      height,
    };
  }, []);

  const getCustomLayoutSettings = useCallback(
    (): Partial<LayoutSettings<FlowchartNode, Edge>> => ({
      snapToGrid: SNAP_GRID,
      algorithm: LayoutAlgorithmName.ELK,
      startNodeId: startNode?.id,
      endNodeId: endNode?.id,
      calculateEdgeLabelDimensions,
      calculateNodeDimensions: (node) => getMeasuredDimensionsFromStore(node.id),
      horizontalSpaceBetweenNodes: 30,
      elkDirection: isExecutionViewer ? 'DOWN' : DEFAULT_ELK_LAYOUT_DIRECTION,
      elkLayoutOptions: {
        'elk.layered.cycleBreaking.strategy': 'DEPTH_FIRST',
        'elk.layered.layering.strategy': 'NETWORK_SIMPLEX',
        'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
        'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
      },
    }),
    [startNode, endNode, calculateEdgeLabelDimensions, getMeasuredDimensionsFromStore, isExecutionViewer]
  );

  useEffect(() => {
    if (hasPerformedInitialAutolayout.current) {
      return;
    }
    const handleVisibilityChange = (): void => {
      if (document.hidden) {
        wasHiddenDuringRenderRef.current = true;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    // Only perform initial autolayout once, not every time nodes change
    if (interactiveNodes.length === 0 || hasPerformedInitialAutolayout.current) {
      return;
    }

    if (!hasLoggedNodesRenderedRef.current) {
      hasLoggedNodesRenderedRef.current = true;
      logProfilerTimingEvent(
        {...FLOWCHART_CONFIG_INITIAL_LOAD_DATA_ENDED_EVENT, id: flowchartConfigId},
        composeFlowchartEventMetadata({
          flowchartType: flowchartTypeName,
          numNodes: interactiveNodes.length,
          ...countEdgesByType(edgesForLayout),
          editorMode,
          flowchartConfigId,
        })
      );
      logProfilerTimingEvent({...FLOWCHART_CONFIG_INITIAL_RENDER_STARTED_EVENT, id: flowchartConfigId});
    }

    // Wait for all nodes to be ready (handles + dimensions) before running autolayout.
    if (!allNodesReadyForAutolayout) {
      return;
    }

    const customLayoutSettings = getCustomLayoutSettings();

    layoutFlowchart(
      {
        nodes: interactiveNodes,
        edges: edgesForLayout,
        getHandles,
      },
      customLayoutSettings
    ).then(({nodes: layoutNodes, edges: layoutEdges}) => {
      setNodesAndEdgesViaChangesForLayout(layoutNodes, layoutEdges);
      hasPerformedInitialAutolayout.current = true;
      setPerformInitialFitView(true);
      const metadata = composeFlowchartEventMetadata({
        flowchartType: flowchartTypeName,
        numNodes: layoutNodes.length,
        ...countEdgesByType(layoutEdges),
        editorMode,
        flowchartConfigId,
        wasHiddenDuringRender: wasHiddenDuringRenderRef.current,
      });
      logProfilerTimingEvent({...FLOWCHART_CONFIG_INITIAL_LOAD_ENDED_EVENT, id: flowchartConfigId}, metadata);
      logProfilerTimingEvent(
        {...FLOWCHART_CONFIG_INITIAL_RENDER_ENDED_EVENT, id: flowchartConfigId},
        metadata
      );
    });
  }, [
    interactiveNodes,
    edgesForLayout,
    allNodesReadyForAutolayout,
    setNodesAndEdgesViaChangesForLayout,
    setPerformInitialFitView,
    getHandles,
    getCustomLayoutSettings,
    flowchartTypeName,
    editorMode,
    flowchartConfigId,
  ]);

  useEffect(() => {
    if (!performInitialFitView) {
      return;
    }
    setTimeout(() => {
      reactFlowInstance.fitView({...DEFAULT_FIT_VIEW_OPTIONS, maxZoom: FIT_VIEW_MAX_ZOOM});
    });
    setPerformInitialFitView(false);
  }, [performInitialFitView, reactFlowInstance, setPerformInitialFitView]);

  const {isLayoutInProgress, runWhenLayoutReady} = useWaitForLayoutReady({
    nodes: interactiveNodes,
    edges: edgesForLayout,
    getHandles,
  });

  // Hide nodes and edges before initial layout completes.
  // Note: `performInitialFitView` is included in the below dependencies as a proxy for when
  // `hasPerformedInitialAutolayout.current` changes. The ref is used in the layout effect but we need
  // these memos to recalculate when autolayout completes.
  const nodesToRender = useMemo(() => {
    if (hasPerformedInitialAutolayout.current) {
      return interactiveNodes;
    }
    return interactiveNodes.map((node) => hideFlowchartElementBeforeLayout(node));
    // `performInitialFitView` is an extra dependency, see note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactiveNodes, performInitialFitView]);

  const edgesToRender = useMemo(() => {
    if (!hasPerformedInitialAutolayout.current) {
      return edgesForLayout.map((edge) => hideFlowchartElementBeforeLayout(edge));
    }
    return edgesForLayout;
    // `performInitialFitView` is an extra dependency, see note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edgesForLayout, performInitialFitView]);

  return {
    nodesToRender,
    edgesToRender,
    startNode,
    endNode,
    getCustomLayoutSettings,
    getHandles,
    isLayoutInProgress,
    runWhenLayoutReady,
  };
}
